<template>
<div>
  <my-header></my-header>

  <div v-if="isLoadedNormal" class="content-container-level-1">
    <h2>Новая регата</h2>

    <b-form @submit.prevent="createRegattasWithConfirmation">

      <b-form-group
        label="Начало (UTC+3)"
        label-for="start"
        label-class="mb-1"
      >
        <moment-input-table id="start" v-model="startDate" :min="minStartDate" class="mb-3"/>
      </b-form-group>

      <h5 class="mb-1">Длительность</h5>
      <fieldset class="form-group pb-1">
        <duration-input v-model="duration"></duration-input>
      </fieldset>

      <p class="mb-4">Конец: {{ firstRegattaEndSec | getMomentMinStr }} (UTC+3)</p>

      <b-form-group
        label="Минимальная версия клиента"
        label-for="min-client-version"
        label-class="mb-1"
      >
        <b-form-input id="min-client-version" v-model="minClientVersion" type="text" required></b-form-input>
        <small id="forced-version" class="form-text text-muted">Min value (forced version): {{ forcedVersion }}</small>
        <small id="server-version" class="form-text text-muted">Max value (server version): {{ serverVersion }}</small>
      </b-form-group>

      <b-form-group
          label="Сегмент"
          label-for="audience"
          label-class="mb-1"
      >
        <b-form-select
            v-model="audience"
            :options="audiences"
        />
      </b-form-group>

      <b-form-group
          label="АБ тест"
          label-for="abTestId"
          label-class="mb-1"
      >
        <b-form-select
            v-model="abTestId"
            :options="abTestOptions"
            value-field="proto_id"
            text-field="alias"
        />
      </b-form-group>
      <b-form-group
          label="Группа аб теста"
          label-for="abtestGroup"
          label-class="mb-1"
      >
        <b-form-select
            v-model="abtestGroup"
            :options="abtestGroupOptions"
            :disabled="abtestGroupDisabled"
        />
      </b-form-group>
      <hr>

      <h5>
        <b-form-checkbox v-model="isRepeatRequired">
          Повторять по правилам
        </b-form-checkbox>
      </h5>

      <template v-if="isRepeatRequired">
        <div class="mb-3">
          <label for="pause" class="mb-1">Пауза между регатами (в часах)</label>
          <input id="pause" type="number" min="0" class="form-control" v-model="pauseHourStr">
        </div>

        <p class="mb-1">Повторять до даты (UTC+3)</p>
        <moment-input-table
            v-model="repeatEndMoment"
            :min="repeatEndMinMoment"
            class="mb-3"
        />

        <template v-if="optionalPeriods !== null">
          <p class="mb-1">Все регаты, которые будут созданы.</p>
          <periods-table
              :periods="optionalPeriods"
          />
        </template>

      </template>

      <hr>

      <b-button :to="{name: 'regatta_list'}">Cancel</b-button>
      <b-button type="submit" variant="primary">Create</b-button>
    </b-form>
  </div>

  <loading-indicator v-if="isLoading"/>

</div>
</template>

<script>
import MyHeader from '../Header.vue';
import LoadingIndicator from '../LoadingIndicator.vue';
import DurationInput from '../DurationInput.vue';
import MomentInputTable from '../util/MomentInputTable.vue';

import { HTTP } from '../../http-common.js';
import notifier from '../../notifier.js';

/**
 * @typedef { import('../../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../../service/confirm.js';

import { convertVersionCodeToStr } from '../../util.js';
import { convertHourToSec        } from '../../util.js';
import { parseUint32             } from '../../util.js';
import { convertMomentToStamp    } from '../../util.js';
import { Period                  } from '../../util.js';
import { getRepeatedPeriods      } from '../../util.js';
import {DEFAULT_REGATTA_AUDIENCE, getRegattaAudienceVerbose} from "@/model/regatta";
import PeriodsTable from "@/components/PeriodsTable.vue";

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

export default {
  name: 'RegattaAdd',

  components: {
    PeriodsTable,
    MyHeader,
    LoadingIndicator,
    DurationInput,
    MomentInputTable,
  },

  data() {
    const nowSec = Math.floor(Date.now() / 1000);

    // Округляем время до 5 минут.
    const fiveMin = 5 * 60;
    const roundedNowSec = nowSec - (nowSec % fiveMin);

    const beginSec = roundedNowSec;
    const beginMSec = beginSec * 1000;
    const beginMoment = new Date(beginMSec);

    const minStartDate = new Date(beginMSec);

    // По умолчанию длительность проведения скачек: 24 часа.
    const DEFAULT_DURATION_HOURS = 24;
    const DEFAULT_DURATION_SEC = convertHourToSec(DEFAULT_DURATION_HOURS);

    const DEFAULT_PROHIBITION_DURATION_HOURS = 10;
    const DEFAULT_PROHIBITION_DURATION_SEC = convertHourToSec(DEFAULT_PROHIBITION_DURATION_HOURS);

    const repeatEndSec = beginSec + DEFAULT_DURATION_SEC;
    const repeatEndMoment = new Date(repeatEndSec * 1000);

    return {
      loadingStatus: LOADING_STATUS__INIT,

      newLeague: null,

      minStartDate: minStartDate,
      startDate:    beginMoment,

      // продолжительность скачек в секундах
      duration: DEFAULT_DURATION_SEC,

      // Продолжительность периода,
      // в течение которого игрок уже не может присоединиться к скачкам, в секундах.
      prohibitionInterval: DEFAULT_PROHIBITION_DURATION_SEC,

      // По умолчанию ограничение по уровню составляет 40 уровней.
      minPlayerLevel: 40,

      minClientVersion: '',

      audience: DEFAULT_REGATTA_AUDIENCE,
      audiences: getRegattaAudienceVerbose(),

      abTestId: 0,
      abTestOptions: [],
      abtestGroup: '',
      abtestGroupOptions: ["A", "B"],
      abtestGroupDisabled: true,

      forcedVersion: '',
      serverVersion: '',

      isRepeatRequired: false,
      pauseHourStr: '24',

      repeatEndMoment: repeatEndMoment,
      repeatEndMinMoment: beginMoment,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    // Возвращает момент времени в секундах.
    joinAvailableUntil() {
      return this.firstRegattaEndSec - this.prohibitionInterval;
    },

    // Возвращает момент времени в секундах.
    firstRegattaBeginSec() {
      return Math.floor(this.startDate.getTime() / 1000);
    },

    // Возвращает момент времени в секундах.
    firstRegattaEndSec() {
      return this.firstRegattaBeginSec + this.duration;
    },

    /** @returns {Object} */
    minPlayerLevelClass() {
      return {
        muted: this.minPlayerLevel === 0,
      };
    },

    /** @returns {[?Period, ?Error]} */
    validFirstStampPeriod() {
      const begin = this.firstRegattaBeginSec;
      const end   = this.firstRegattaEndSec;

      if(end === begin) {
        return [null, new Error('Contest duration is zero.')];
      }
      if(end <= begin) {
        return [null, new Error('End time must be after begin time.')];
      }

      return [new Period(begin, end), null];
    },

    /** @returns {[?number, ?Error]} */
    pauseSec() {
      const [pauseHour, err] = this.pauseHour;
      if(err !== null) {
        return [null, err];
      }
      if(pauseHour === null) {
        return [null, new Error('pauseHour === null')];
      }

      const pauseSec = convertHourToSec(pauseHour);
      return [pauseSec, null];
    },

    /** @returns {[?number, ?Error]} */
    pauseHour() {
      const pauseHour = parseUint32(this.pauseHourStr);
      if(pauseHour === null) {
        return [null, new Error('Pause between events is invalid.')];
      }
      return [pauseHour, null];
    },

    /** @returns {[?number, ?Error]} */
    repeatEndStamp() {
      const repeatEndMoment = this.repeatEndMoment;
      if(repeatEndMoment === null) {
        return [null, new Error('Repeat end moment is invalid.')];
      }

      const stamp = convertMomentToStamp(repeatEndMoment);
      return [stamp, null];
    },

    /** @returns {?Period[]} */
    optionalPeriods() {
      const [periods, err] = this.periods;
      if(err !== null) {
        return null;
      }
      return periods;
    },

    /** @returns {[?Period[], ?Error]} */
    periods() {
      /** @type {?Period} */
      let firstPeriod;

      /** @type {?Error} */
      let err;

      [firstPeriod, err] = this.validFirstStampPeriod;
      if(err !== null) {
        return [null, err];
      }
      if(firstPeriod === null) {
        return [null, new Error('firstPeriod === null')];
      }

      if(!this.isRepeatRequired) {
        const periods = [firstPeriod];
        return [periods, null];
      }

      /** @type {?number} */
      let pauseSec;

      [pauseSec, err] = this.pauseSec;
      if(err !== null) {
        return [null, err];
      }
      if(pauseSec === null) {
        return [null, new Error('pauseSec === null')];
      }

      /** @type {?number} */
      let repeatEndStamp;
      [repeatEndStamp, err] = this.repeatEndStamp;
      if(err !== null) {
        return [null, err];
      }
      if(repeatEndStamp === null) {
        return [null, new Error('repeatEndStamp === null')];
      }

      if(firstPeriod.end > repeatEndStamp) {
        return [null, new Error('First period end must be before repeat end.')];
      }

      const periods = getRepeatedPeriods(firstPeriod, pauseSec, repeatEndStamp);
      return [periods, null];
    },
  },

  created() {
    this.loadAddingInfo();
    this.loadAbTestInfo();
  },

  watch: {
    abTestId(newId) {
      if (newId === 0) {
        this.abtestGroupDisabled = true
        this.abtestGroup = ""
      } else {
        this.abtestGroupDisabled = false
      }
    },
  },

  methods: {
    loadAddingInfo() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      HTTP.get('/regattas/adding_info')
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        const forcedVersion =  convertVersionCodeToStr(data.forced_version);
        this.forcedVersion = forcedVersion;

        this.serverVersion = convertVersionCodeToStr(data.server_version);

        this.minClientVersion = forcedVersion;

        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    loadAbTestInfo() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      HTTP.get('/configs/search-ab-test', {params: {q: 1}})
          .then(response => {
            const data = response.data.data;
            if(data === null) {
              notifier.showError('Invalid server response. ab test data === null.', {sender: this});
            }
            data.unshift({"proto_id":0, "alias": "---"})
            this.abTestOptions = data
          })
          .catch(err => {
            this.loadingStatus = LOADING_STATUS__ERROR;
            notifier.showError(err, {sender: this});
          });
    },

    createRegattasWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const [regattasDef, err] = this.getRegattasDef();
      if(err !== null) {
        notifier.showError(err, {sender: this});
        return;
      }
      if(regattasDef === null) {
        notifier.showError('Invalid input report. derbiesDef === null.', {sender: this});
        return;
      }

      const regattasAmount = regattasDef.periods.length;

      this.confirmRegattasCreation(
        regattasAmount,
        () => {
          this.createRegattas(regattasDef);
        },
      );
    },

    /**
     * @param {number} regattasAmount
     * @param {ConfirmHandle} onConfirmed
     */
    confirmRegattasCreation(regattasAmount, onConfirmed) {
      const target = (regattasAmount === 1) ? 'this regatta' : regattasAmount.toString() + ' regattas';

      confirmAction(
        this,
        `Are you sure you want to create ${target} {env}?`,
        'Create',
        onConfirmed,
      );
    },

    createRegattas(regattasDef) {
      const dataStr = JSON.stringify(regattasDef);

      const url = '/regattas/add';

      HTTP.post(url, dataStr)
      .then(response => {
        const result = response.data.result;

        if(result.code > 0) {
          notifier.showError(result.message, {sender: this});
          return;
        }

        notifier.showSuccessAfterTransition('Regatta added.');
        this.$router.push({name: 'regatta_list'});
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    getRegattasDef() {
      const [periods, err] = this.periods;
      if(err !== null) {
        return [null, err];
      }
      if(periods === null) {
        return [null, new Error('periods === null')];
      }

      const regattasDef = {
        prohibitionInterval: this.prohibitionInterval,
        minPlayerLevel:      this.minPlayerLevel,
        minClientVersion:    this.minClientVersion,
        leagues:             this.leagues,
        periods:             periods,
        audience:            this.audience,
        abTestId:            this.abTestId,
        abTestGroup:         this.abtestGroup,
      };

      return [regattasDef, null];
    },
  },
}
</script>

<style scoped>
fieldset.form-group {
  border: 1px solid #ced4da;
  border-radius: 0.25em;
  margin-left: 0;
  margin-right: 0;
}

fieldset > legend {
  font-size: 1.25em;
}

caption {
  caption-side: top;
}

.muted,
.muted:focus {
  color: #dcdcdc;
}
</style>